/**
 * The tenant's groups (D14): name, scope in words, how many permissions and
 * members; open one (the group editor), create one. `groups`, `loading` and
 * `error` are independent props.
 */
import type { Group } from '@acp/api-contract/tenancy.js';
import { PlusIcon, RefreshCwIcon } from 'lucide-react';
import { scopeText } from '../../lib/admin/view.js';
import { isPlainClick } from '../../lib/links.js';
import { Button } from '../ui/button.js';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.js';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table.js';

export type GroupsTableProps = {
  readonly groups: readonly Group[];
  /** Members per group id (from the users list). */
  readonly memberCounts: ReadonlyMap<string, number>;
  readonly names: { readonly sites: ReadonlyMap<string, string>; readonly studios: ReadonlyMap<string, string> };
  readonly loading: boolean;
  readonly error: string | null;
  readonly onCreate: () => void;
  readonly onRetry: () => void;
  readonly onOpen: (groupId: string) => void;
};

export function groupHref(groupId: string): string {
  return `/admin/groups/${encodeURIComponent(groupId)}`;
}

export function GroupsTable({ groups, memberCounts, names, loading, error, onCreate, onRetry, onOpen }: GroupsTableProps) {
  return (
    <Card data-testid="groups-table">
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <div className="grid gap-1.5">
          <CardTitle>Groups</CardTitle>
          <CardDescription>A group grants its permissions within its scope: the whole tenant, or chosen sites and studios.</CardDescription>
        </div>
        <Button onClick={onCreate} data-testid="create-group">
          <PlusIcon />
          New group
        </Button>
      </CardHeader>
      <CardContent>
        {error === null ? null : (
          <div role="alert" className="flex items-center justify-between gap-4 rounded-md border border-destructive p-4 text-sm">
            <span className="text-destructive">Could not load groups: {error}</span>
            <Button variant="outline" size="sm" onClick={onRetry}>
              <RefreshCwIcon />
              Retry
            </Button>
          </div>
        )}
        {loading && groups.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground">Loading groups…</p> : null}
        {groups.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Scope</TableHead>
                <TableHead className="w-28">Permissions</TableHead>
                <TableHead className="w-24">Members</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {groups.map((g) => (
                <TableRow key={g.groupId} data-testid="group-row">
                  <TableCell className="font-medium">
                    <a
                      href={groupHref(g.groupId)}
                      className="hover:underline"
                      data-testid="open-group"
                      onClick={(event) => {
                        if (!isPlainClick(event)) return;
                        event.preventDefault();
                        onOpen(g.groupId);
                      }}
                    >
                      {g.name}
                    </a>
                  </TableCell>
                  <TableCell className="text-sm">{scopeText(g.scope, names)}</TableCell>
                  <TableCell className="text-sm">{g.permissions.length}</TableCell>
                  <TableCell className="text-sm">{memberCounts.get(g.groupId) ?? 0}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : null}
      </CardContent>
    </Card>
  );
}
